import { Button, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom/dist";
import usePostData from "../../../api/usePostData";
import Heading from "../../../components/Heading/Heading";
import {
  getNewTime,
  resetExpiration,
} from "../../../utils/getOTPExpirationTime";
import { RestPasswordTracking } from "../../../utils/mixpanel";

/**
 *
 * @param {Object} props
 * @param {number} timer - A  controlled state that represents the time remaining until the user can resend OTP to the specified email
 * @param {React.SetStateAction<number>} setTimer - A set state for the timer state
 * @returns
 */
export default function OTPStep({ timer, setTimer }) {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const { postData, loading: sendingRequest, errors } = usePostData();
  const { postData: resetPassword, errors: resettingErrors } = usePostData();
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || ""; // Fallback to empty string if not found

  const {
    t,
    i18n: { language },
  } = useTranslation("global");

  const handleVerify = useCallback(async () => {
    if (sendingRequest) {
      return;
    }
    const data = {
      email: email,
      otp: otp,
    };
    const response = await postData({
      route: "api/method/airport_transport.api.user.confirm_email",
      data,
    });

    if (response?.status === 200) {
      navigate(`/forget-password?email=${email}`, {
        state: { otpStep: false, email: email, otp: otp },
      });
      resetExpiration();
      setTimer(0);
    } else {
      RestPasswordTracking(false, "Invalid OTP");
    }
  }, [otp]);

  useEffect(() => {
    if (errors) {
      const errorMsg = errors?.error?.toLowerCase();
      if (errorMsg === "expired otp") {
        message.error(t("errors.expired_otp"));
        setError(t("errors.expired_otp"));
      } else if (errorMsg === "invalid otp") {
        message.error(t("errors.invalid_otp"));
        setError(t("errors.invalid_otp"));
      } else if (errorMsg === "'otp' is required") {
        setError(t("errors.otp_required"));
      } else {
        message.error(`${errorMsg}`);
        setError(`${errorMsg}`);
      }
    }
  }, [errors]);

  const handleChange = (value) => {
    setOtp(value);
    setError(""); // Clear error message when user types
  };

  useEffect(() => {
    if (otp.length === 6) {
      handleVerify();
    }
  }, [otp, handleVerify]);

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setShowResend(true); // Show resend button when timer completes
      const optInputs = document.getElementsByClassName("forgot-otp-input");
      for (let input of optInputs) {
        input.disabled = true;
      }
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
    try {
      const data = {
        email: email,
      };
      const otpResponse = await resetPassword({
        data,
        route: "api/method/airport_transport.api.user.reset_password",
      });
      // Redirect to OTP verification screen
      if (otpResponse?.status === 200) {
        setOtp("");
        setError("");
        const optInputs = document.getElementsByClassName("forgot-otp-input");
        for (let input of optInputs) {
          input.disabled = false;
        }
        const newTime = getNewTime();
        setTimer(newTime); // Reset timer
        setShowResend(false); // Hide resend button
        message.success(t("otp_resent"));
      }
    } catch (error) {
      message.error(`${error?.response?.data?.msg}`);
    }
  };

  useEffect(() => {
    if (resettingErrors?.error) {
      if (
        resettingErrors.error ===
        "Too many otp requests in a short while. Wait a minute..."
      ) {
        message.error(t("errors.retries_otp"));
      } else {
        message.error(`${resettingErrors.error}`);
      }
    }
  }, [resettingErrors]);

  const goBackToSetNewEmail = () => {
    navigate(`/forget-password`, { replace: true, state: {} });
  };
  return (
    <>
      <div className="sm:w-screen w-full position relative">
        <div
          className="position absolute left-0 top-0"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <img
            src="./assets/signin/left_vector.png"
            alt="left_vector"
            className="w-24 h-24 md:w-48 md:h-48"
          />
        </div>
        <div
          className="position absolute right-0 bottom-0"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <img
            src="./assets/signin/right_vector.png"
            alt="right_vector"
            className="w-24 md:w-48 h-18 md:h-36"
          />
        </div>
        <div className="z-20 sm:w-screen w-full min-h-screen flex flex-row justify-center items-center">
          <div className="flex flex-col justify-center items-center">
            <Link
              className="mb-4 cursor-pointer"
              dir={language === "ar" ? "rtl" : "ltr"}
              to={"/"}
            >
              <img
                src="./assets/header/LogoSVG.svg"
                alt="Moshrouk Trips"
                className="w-16 h-13"
              />
            </Link>
            <div className="mt-4 block w-72 md:w-auto p-3 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <div
                className="w-auto md:w-[400px] text-left rtl:text-right p-4"
                dir={language === "ar" ? "rtl" : "ltr"}
              >
                <Heading
                  title={t("otp_verification_text")}
                  className={"text-2xl text-[#0E0E11]"}
                />
                <div className="w-auto md:w-3/4">
                  <p className="text-sm text-[#5D5D5D]">
                    {t("enter_6_digit_text")}
                    <span className="ml-1 text-text_steel_blue">
                      {email ? email : "User@gmail.com"}
                    </span>{" "}
                    <button
                      className=" text-text_steel_blue underline font-bold cursor-pointer ml-1"
                      onClick={() => {
                        goBackToSetNewEmail();
                      }}
                    >
                      ({t("change_text")})
                    </button>
                  </p>
                </div>
              </div>

              <div>
                <div className="p-2 md:p-4">
                  <OtpInput
                    inputStyle="otp-input"
                    value={otp}
                    onChange={handleChange}
                    numInputs={6}
                    renderInput={(props, i) => <input {...props} key={i} />}
                  />

                  {error && (
                    <div
                      className="text-base text-text_warning font-semibold my-2"
                      dir={language === "ar" ? "rtl" : "ltr"}
                    >
                      {error}
                    </div>
                  )}

                  <div
                    className="mt-2 flex flex-row justify-start items-center"
                    dir={language === "ar" ? "rtl" : "ltr"}
                  >
                    <div className="mr-1">
                      {showResend ? (
                        <Button
                          className="bg-background_steel_blue w-20 text-center text-text_white hover:bg-gray-100 font-medium rounded text-sm px-1 py-2 me-2 mb-2"
                          type="button"
                          htmlType="button"
                          onClick={handleResend}
                        >
                          {t("resend_text")}
                        </Button>
                      ) : (
                        `${t("hero.resend_otp_text")} - ${Math.floor(timer / 60)
                          .toString()
                          .padStart(2, "0")}:${(timer % 60)
                          .toString()
                          .padStart(2, "0")}`
                      )}
                    </div>
                  </div>

                  <div className="my-3" dir={language === "ar" ? "rtl" : "ltr"}>
                    <Button
                      className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 me-2 mb-2"
                      type="submit"
                      htmlType="submit"
                      onClick={handleVerify}
                    >
                      {t("verify_text")}
                    </Button>
                  </div>

                  <div
                    className="text-sm text-background_steel_blue cursor-pointer"
                    dir={language === "ar" ? "rtl" : "ltr"}
                  >
                    {t("problems_text")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
